import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { money, currencySymbol } from '../../utils/region';
import Pagination from '../Pagination';
import { MODULES, SHARED_MODULES, CONTAINER_SUBS, CONTAINER_SUB_KEYS, moduleIcon } from '../../modules';
import { AdminUser, Company, Role } from '../../types';
import CompanySettings, { WorkspaceSettings } from './CompanySettings';
import BankMailboxSettings from './BankMailboxSettings';
import ConsultCalendarSettings from './ConsultCalendarSettings';
import { MarketCitiesCard, ClientRoleHint } from './FirmContactSettings';
import CountryFeaturesCard from './CountryFeaturesCard';   // cpackdocs
import PoaMadeAt from './PoaMadeAt';   // Admin → Companies: the firm's wording for a POA's Made at (report 404df7d2)
import Roles from './Roles';
import { useAdminOnlyModules } from './ModuleAccess';
import EmailTemplates from './EmailTemplates';
import KnowledgeBase from './KnowledgeBase';
import PeopleSettings from './PeopleSettings';   // aliases, greetings, personal mailboxes (package people)
import NameCheckAllowances from './NameCheckAllowances';   // Filing name checks: a person's own allowance a release, weekends too
import PortalClients from './PortalClients';
import ErrorAlerts from './ErrorAlerts';
import JobResPills from './JobResPills';
import JobRequiredDocs from './JobRequiredDocs';
import RemovedJobs from './RemovedJobs';   // Admin → Jobs: put a soft-deleted job back on the board (report 3506b2cc)
import ThaiHolidays from './ThaiHolidays';
import ContactBranches from './ContactBranches';
import ChatDocGroup from './ChatDocGroup';   // Admin → Chat: which group is the document upload group
import ChatPins from './ChatPins';           // …and which groups are held at the top of the chat list
import ChatJobsGroup from './ChatJobsGroup'; // …and which group Active Jobs comments go to (report 8e70cd7c)
import { usePicDoor, keepPic } from '../Chat/ChatAvatar';   // a person's picture in Chat (report 42100db4)
import TeamCalendars from './TeamCalendars';
import TelegramBind from '../Tax/TelegramBind';
import { brandOr } from '../../utils/brand';
import { isAccountingFirm } from '../../utils/firmKind';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none',
};
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color,
  fontSize: 13, fontWeight: 600, cursor: 'pointer',
});

// ── Employee create/edit modal ─────────────────────────────────────────────
// ── A colleague's mailboxes (owner 2026-09-20) ──────────────────────────────────────────────────
// Until now an admin could not touch anyone's mailbox but their own, so setting up a new member of
// staff meant they did it themselves or somebody edited the production database. The password is
// typed HERE, by the person doing the setup — it is never stored anywhere else and never shown
// back; existing mailboxes report only whether one is set.
//
// The firm's own servers are the defaults, including SMTP on 2525, which is the setting people get
// wrong: the form's usual default of 587 does not work here and the failure only shows on the first
// attempt to SEND.
const MX = { imapHost: 'chocobo.mxrouting.net', imapPort: 993, smtpHost: 'chocobo.mxrouting.net', smtpPort: 2525 };

function Mailboxes({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: '', email: '', password: '', ...MX });

  const load = () => api.adminMailboxes(userId).then((d: any) => setRows(d.mailboxes || [])).catch((e: any) => setErr(e.message));
  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  const add = async () => {
    setErr(''); setBusy('add');
    try {
      await api.adminMailboxAdd(userId, { ...f, name: f.name || f.email });
      setF({ name: '', email: '', password: '', ...MX }); setAdding(false); load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const toggleSync = async (m: any) => {
    setErr(''); setBusy(m.id);
    try { await api.adminMailboxPatch(m.id, { syncEnabled: !m.syncEnabled }); load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const remove = async (m: any) => {
    const typed = window.prompt(t('mb_delete_confirm', { email: m.email, n: m.emails }));
    if (!typed) return;
    setErr(''); setBusy(m.id);
    try { await api.adminMailboxDelete(m.id, typed); load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const inp: React.CSSProperties = { padding: '7px 9px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: 12.5, width: '100%', boxSizing: 'border-box' };
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-tertiary)', marginBottom: 8 }}>{t('mb_title')}</div>
      {rows === null ? <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
        : rows.length === 0 && !adding ? <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>{t('mb_none')}</div>
        : rows.map(m => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 12.5, flexWrap: 'wrap' }}>
            <i className={`ti ti-${m.syncEnabled ? 'mail' : 'mail-off'}`} style={{ color: m.syncEnabled ? 'var(--accent)' : 'var(--text-tertiary)' }} />
            <span style={{ fontWeight: 700 }}>{m.email}</span>
            <span style={{ color: 'var(--text-tertiary)' }}>{m.imapHost}</span>
            <span style={{ color: 'var(--text-tertiary)' }}>{t('mb_emails', { n: m.emails })}</span>
            {!m.hasPassword && !m.oauth && <span style={{ color: '#B45309' }}>{t('mb_no_password')}</span>}
            {m.oauth && <span style={{ color: 'var(--text-tertiary)' }}>OAuth: {m.oauth}</span>}
            {!m.hasSignature && <span style={{ color: '#B45309' }}>{t('mb_no_signature')}</span>}
            <div style={{ flex: 1 }} />
            <button onClick={() => toggleSync(m)} disabled={busy === m.id}
              style={{ ...btn(m.syncEnabled ? 'var(--c-gray-100)' : 'var(--accent)', m.syncEnabled ? 'var(--text-secondary)' : undefined), padding: '4px 10px', fontSize: 12 }}>
              {m.syncEnabled ? t('mb_sync_off') : t('mb_sync_on')}
            </button>
            <button onClick={() => remove(m)} disabled={busy === m.id} style={{ ...btn('#FCEBEB', '#E24B4A'), padding: '4px 10px', fontSize: 12 }}>{t('delete')}</button>
          </div>
        ))}

      {adding ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7, marginTop: 10 }}>
          <input placeholder={t('mb_email')} value={f.email} onChange={e => setF({ ...f, email: e.target.value })} style={inp} />
          <input placeholder={t('mb_display_name')} value={f.name} onChange={e => setF({ ...f, name: e.target.value })} style={inp} />
          <input type="password" placeholder={t('mb_password')} value={f.password} onChange={e => setF({ ...f, password: e.target.value })} style={{ ...inp, gridColumn: '1 / -1' }} />
          <input placeholder="IMAP" value={f.imapHost} onChange={e => setF({ ...f, imapHost: e.target.value })} style={inp} />
          <input placeholder="IMAP port" value={f.imapPort} onChange={e => setF({ ...f, imapPort: Number(e.target.value) })} style={inp} />
          <input placeholder="SMTP" value={f.smtpHost} onChange={e => setF({ ...f, smtpHost: e.target.value })} style={inp} />
          <input placeholder="SMTP port" value={f.smtpPort} onChange={e => setF({ ...f, smtpPort: Number(e.target.value) })} style={inp} />
          <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--text-tertiary)' }}>{t('mb_hint')}</div>
          {busy === 'add' && <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: '#B45309' }}>{t('mb_testing_wait')}</div>}
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
            <button onClick={() => setAdding(false)} style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), padding: '5px 12px', fontSize: 12 }}>{t('cancel')}</button>
            <button onClick={add} disabled={busy === 'add' || !f.email || !f.password} style={{ ...btn('var(--accent)'), padding: '5px 12px', fontSize: 12 }}>
              {busy === 'add' ? t('mb_testing') : t('mb_add')}
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), padding: '5px 12px', fontSize: 12, marginTop: 9 }}>
          <i className="ti ti-plus" /> {t('mb_add')}
        </button>
      )}
      {err && <div style={{ fontSize: 12, color: '#E24B4A', marginTop: 8 }}>{err}</div>}
    </div>
  );
}

// ── A person's picture in Chat (report 42100db4: "Users or both" set a person's picture) ───────────────────────────
// The administrator's door to the picture the person may also set themselves with My picture in Chat. Saved at once,
// on its own — it is not part of the editor's Save — and shown for active staff only, the people Chat lists.
const NO_CHAT_ROLES = ['client', 'external', 'marketing', 'auditor'];
function ChatPicture({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const [pic, setPic] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    api.chatPicture('user', userId).then(r => { if (live) setPic(String((r && r.url) || '')); }, () => { /* the block offers to set one */ });
    return () => { live = false; };
  }, [userId]);
  const door = usePicDoor(
    async (url: string) => { const r = await api.chatSetPicture('user', userId, url); setPic(url); keepPic('user', userId, r.avatar, url); },
    async () => { await api.chatDropPicture('user', userId); setPic(''); keepPic('user', userId, '', ''); },
    setErr,
  );
  const small: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: door.busy ? 'wait' : 'pointer' };
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>{t('adm_chat_pic')}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {pic
          ? <img src={pic} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
          : <span style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--c-gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <i className="ti ti-user" style={{ fontSize: 20, color: 'var(--text-tertiary)' }} aria-hidden />
            </span>}
        <button type="button" onClick={() => door.pick()} disabled={door.busy} style={small}>
          <i className={`ti ${door.busy ? 'ti-loader' : 'ti-photo'}`} style={{ fontSize: 13, animation: door.busy ? 'spin 1s linear infinite' : undefined }} aria-hidden />
          {t(pic ? 'chat_pic_change' : 'chat_pic_set')}
        </button>
        {pic && (
          <button type="button" onClick={() => void door.remove()} disabled={door.busy} style={{ ...small, color: '#E24B4A' }}>
            <i className="ti ti-trash" style={{ fontSize: 13 }} aria-hidden />{t('chat_pic_remove')}
          </button>
        )}
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('adm_chat_pic_hint')}</div>
      {err && <div style={{ fontSize: 12, color: '#E24B4A', marginTop: 4 }}>{err}</div>}
      {door.node}
    </div>
  );
}

function UserEditor({ user, companies, onClose, onSaved }: {
  user: AdminUser | null; companies: Company[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isNew = !user;
  const [email, setEmail] = useState(user?.email || '');
  const [name, setName] = useState(user?.name || '');
  const [nickname, setNickname] = useState((user as any)?.nickname || '');
  // A third word for a person (Calem, report 079721fa): "a separate 'Full name' box added to each
  // staff record, left to the nickname elsewhere and used only on the Accounting lists". Nothing but
  // the Accounting page's staff-cost rows reads it, and Name and Nickname above are untouched by it.
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [password, setPassword] = useState('');
  const [companyId, setCompanyId] = useState(user?.company_id || '');
  const [allowed, setAllowed] = useState<string[]>(user?.allowedCompanyIds || []);   // may also enter
  const [role, setRole] = useState<Role>(user?.role || 'employee');
  const [active, setActive] = useState(user ? user.active : true);
  // Receives the daily AI improvement suggestions (report 1492665d) — staff seats only (admin / employee).
  const [aiSug, setAiSug] = useState(!!user?.ai_suggestions);
  const [mods, setMods] = useState<string[]>(user?.modules || []);
  const adminOnlyMods = useAdminOnlyModules();   // live module policy (Admin → Roles → Module access)
  const [birthday, setBirthday] = useState((user as any)?.birthday || '');
  const [hireDate, setHireDate] = useState((user as any)?.hire_date || '');
  const [gender, setGender] = useState((user as any)?.gender || '');
  const [linkedin, setLinkedin] = useState((user as any)?.linkedin || '');
  const [facebook, setFacebook] = useState((user as any)?.facebook || '');
  const [instagram, setInstagram] = useState((user as any)?.instagram || '');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const profile = { birthday, hire_date: hireDate, gender, linkedin, facebook, instagram };

  const toggle = (k: string) => setMods(m => m.includes(k) ? m.filter(x => x !== k) : [...m, k]);

  // ── The module grid ─────────────────────────────────────────────────────────────────────────────
  // One checkbox per module the server can actually store. The two CONTAINER pages — {{brand}}™
  // Property and PandaAI™ Marketing — are no permission of their own (modules.ts: CONTAINER_SUBS):
  // the backend catalog has no such key, so a tick on one was dropped on save and gone again on
  // reopen. Each heads a block of its own sub-modules instead, which is where POA has been all along
  // (report 376a385e, asking how a member of staff is given the POA module under Property): tick POA
  // and the Property tile appears on their menu, opening on that tab.
  const grantable = (k: string) => !SHARED_MODULES.includes(k) && !adminOnlyMods.has(k);
  const subsOf = (k: string) => (CONTAINER_SUBS[k] || []).filter(grantable);
  const foldedIntoContainer = new Set(CONTAINER_SUB_KEYS);
  const setMany = (keys: string[], on: boolean) =>
    setMods(m => on ? Array.from(new Set([...m, ...keys])) : m.filter(x => !keys.includes(x)));
  const modCell = (key: string, icon: string) => (
    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-primary)', padding: '4px 6px', borderRadius: 6, background: mods.includes(key) ? 'var(--bg-active)' : 'transparent', cursor: 'pointer' }}>
      <input type="checkbox" checked={mods.includes(key)} onChange={() => toggle(key)} />
      <i className={`ti ti-${icon}`} style={{ fontSize: 14, color: 'var(--accent)' }} aria-hidden />
      {t(`mod_${key}`)}
    </label>
  );

  const staffSeat = role === 'admin' || role === 'employee';
  const save = async () => {
    setErr(''); setSaving(true);
    try {
      if (isNew) {
        if (!email || !password) throw new Error(t('login_fill_fields'));
        const created: any = await api.adminCreateUser({ email, name, nickname, full_name: fullName, password, companyId: companyId || null, allowedCompanyIds: allowed.filter(id => id !== companyId), role, modules: mods, ai_suggestions: aiSug && staffSeat });
        if (created?.id) await api.adminUpdateUser(created.id, profile);   // persist profile on the new user
      } else {
        const patch: any = { name, nickname, full_name: fullName, companyId: companyId || null, allowedCompanyIds: allowed.filter(id => id !== companyId), role, active, modules: mods, ai_suggestions: aiSug && staffSeat, ...profile };
        if (password) patch.password = password;
        await api.adminUpdateUser(user!.id, patch);
      }
      onSaved(); onClose();
    } catch (e: any) { setErr(e.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 520, maxHeight: '88vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>
          {isNew ? t('add_employee') : t('edit') + ' — ' + user!.email}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('emailAddress')}</label>
            <input value={email} disabled={!isNew} onChange={e => setEmail(e.target.value)} style={{ ...inputStyle, opacity: isNew ? 1 : 0.6 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('name')}</label>
            <input value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('adm_nickname')}</label>
            <input value={nickname} onChange={e => setNickname(e.target.value)} placeholder={t('adm_nickname_ph')} style={inputStyle} />
            <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('adm_nickname_hint')}</div>
          </div>
          {/* The third box (report 079721fa). It is read by the Accounting page's staff-cost rows and
              by nothing else — the Name and Nickname above go on being what the rest of the system
              shows — and a record left empty simply has the nickname shown there instead. */}
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('adm_full_name')}</label>
            <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder={t('adm_full_name_ph')} style={inputStyle} />
            <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('adm_full_name_hint')}</div>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{isNew ? t('field_password') : t('reset_password')}</label>
            <input value={password} type="password" placeholder={isNew ? '' : t('leave_blank_keep')} onChange={e => setPassword(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('company')}</label>
              <select value={companyId} onChange={e => setCompanyId(e.target.value)} style={inputStyle}>
                <option value="">{t('none')}</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {/* The other companies this login may switch to from the company menu (owner 2026-10-03).
                  Home is where they land; the server refuses any company not listed here. */}
              {companies.filter(c => c.id !== companyId).length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('adm_also_companies')}</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4, maxHeight: 132, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '6px 8px' }}>
                    {companies.filter(c => c.id !== companyId).map(c => (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-primary)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={allowed.includes(c.id)} onChange={() => setAllowed(a => a.includes(c.id) ? a.filter(x => x !== c.id) : [...a, c.id])} />
                        {c.name}
                      </label>
                    ))}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('adm_also_companies_hint')}</div>
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('role')}</label>
              {/* A client-portal login is not a staff seat: it has a client + merchant tenant behind
                  it, so it is created and managed on the Client Portal tab. Shown here read-only so
                  it can never be turned into (or out of) a staff role by accident. */}
              {role === 'client' ? (
                <div style={{ ...inputStyle, display: 'flex', alignItems: 'center', color: 'var(--text-secondary)', background: 'var(--c-gray-100)' }}>{t('role_client')}</div>
              ) : (
                <select value={role} onChange={e => setRole(e.target.value as Role)} style={inputStyle}>
                  <option value="employee">{t('role_employee')}</option>
                  <option value="admin">{t('role_admin')}</option>
                  <option value="marketing">{t('role_marketing')}</option>
                  <option value="auditor">{t('role_auditor')}</option>
                </select>
              )}
            </div>
          </div>
          {!isNew && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-primary)' }}>
              <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> {t('active')}
            </label>
          )}
          {/* Who receives the daily AI improvement suggestions (report 1492665d): a once-a-day pop-up opening the
              list the AI ranks from past Bugify reports, their fixes and the work staff repeat. A setting per
              person, never a name in the code; staff seats only. */}
          {staffSeat && (
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-primary)' }}>
                <input type="checkbox" checked={aiSug} onChange={e => setAiSug(e.target.checked)} /> {t('adm_ai_suggestions')}
              </label>
              <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('adm_ai_suggestions_hint')}</div>
            </div>
          )}

          {/* Employee profile (Staff Dashboard) — all optional */}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>{t('sd_basic')}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('sd_birthday')}</label><input type="date" value={birthday} onChange={e => setBirthday(e.target.value)} style={inputStyle} /></div>
              <div><label style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('sd_hire_date')}</label><input type="date" value={hireDate} onChange={e => setHireDate(e.target.value)} style={inputStyle} /></div>
              <div><label style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('sd_gender')}</label>
                <select value={gender} onChange={e => setGender(e.target.value)} style={inputStyle}><option value="">—</option><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option></select>
              </div>
              <div><label style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>LinkedIn</label><input value={linkedin} onChange={e => setLinkedin(e.target.value)} style={inputStyle} placeholder="https://…" /></div>
              <div><label style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Facebook</label><input value={facebook} onChange={e => setFacebook(e.target.value)} style={inputStyle} placeholder="https://…" /></div>
              <div><label style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Instagram</label><input value={instagram} onChange={e => setInstagram(e.target.value)} style={inputStyle} placeholder="https://…" /></div>
            </div>
          </div>

          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>{t('modules')}</label>
            {role === 'client' ? (
              <div style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 11px', lineHeight: 1.6 }}><ClientRoleHint /></div>
            ) : role === 'admin' ? (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('admin_all_modules')}</div>
            ) : role === 'marketing' ? (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>{t('role_marketing_modules')}</div>
            ) : role === 'auditor' ? (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>{t('role_auditor_modules')}</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {MODULES.filter(m => CONTAINER_SUBS[m.key]
                    ? subsOf(m.key).length > 0                                      // a heading, while it has anything to offer
                    : grantable(m.key) && !foldedIntoContainer.has(m.key))          // a plain row, unless a container folds it in
                    .map(m => CONTAINER_SUBS[m.key] ? (
                      <div key={m.key} style={{ gridColumn: '1 / -1', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '6px 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <i className={`ti ti-${m.icon}`} style={{ fontSize: 14, color: 'var(--accent)' }} aria-hidden />
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{t(`mod_${m.key}`)}</span>
                          {/* Tick-all / untick-all for exactly this container's sub-modules — the container
                              key itself is never sent. */}
                          <button type="button" onClick={() => setMany(subsOf(m.key), !subsOf(m.key).every(k => mods.includes(k)))}
                            style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                            {subsOf(m.key).every(k => mods.includes(k)) ? t('adm_mod_untick_all') : t('adm_mod_tick_all')}
                          </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                          {subsOf(m.key).map(k => modCell(k, moduleIcon(k)))}
                        </div>
                      </div>
                    ) : modCell(m.key, m.icon))}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 8 }}>{t('contacts_shared')}</div>
              </>
            )}
          </div>

          {/* Mailboxes only once the user exists — they hang off a user id. */}
          {!isNew && user && <Mailboxes userId={user.id} />}

          {/* Their picture in Chat (report 42100db4) — active staff only, the people Chat lists. */}
          {!isNew && user && user.active && !NO_CHAT_ROLES.includes(String(user.role || '')) && <ChatPicture userId={user.id} />}

          {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{err}</div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={onClose} style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('cancel')}</button>
            <button onClick={save} disabled={saving} style={btn('var(--accent)')}>{saving ? t('login_wait') : t('save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Admin panel ─────────────────────────────────────────────────────────────
export default function Admin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useStore(s => s.me);
  const [tab, setTab] = useState<'users' | 'companies' | 'roles' | 'jobs' | 'holidays' | 'contacts' | 'chat' | 'notify' | 'templates' | 'kb' | 'portal' | 'alerts' | 'audit'>('users');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [newCompany, setNewCompany] = useState('');
  const [err, setErr] = useState('');
  // Owner 2026-08-29: staff seats and client-portal logins share one table, and portal logins are
  // about to multiply. Filter by role, and search — the list is no longer something you can scan.
  const [roleF, setRoleF] = useState<'all' | 'admin' | 'employee' | 'marketing' | 'client'>('all');
  const [userQ, setUserQ] = useState('');

  const load = async () => {
    try {
      const [c, u] = await Promise.all([api.adminGetCompanies(), api.adminGetUsers()]);
      setCompanies(c); setUsers(u);
    } catch (e: any) { setErr(e.message); }
  };
  useEffect(() => { load(); }, []);   // eslint-disable-line

  const companyName = (id: string | null) => companies.find(c => c.id === id)?.name || '—';
  // A role that is not a staff seat must never render as "Employee" — that is how a client-portal
  // login came to look like a member of staff in this table.
  const roleLabel = (r: string) => r === 'admin' ? t('role_admin')
    : r === 'marketing' ? t('role_marketing')
    : r === 'auditor' ? t('role_auditor')
    : r === 'client' ? t('role_client')
    : r === 'external' ? t('role_external')
    : t('role_employee');
  const isPortalLogin = (r: string) => r === 'client';
  const roleCount = (r: string) => users.filter(u => (r === 'employee' ? (u.role !== 'admin' && u.role !== 'marketing' && u.role !== 'client') : u.role === r)).length;
  const shownUsers = users.filter(u => {
    const inRole = roleF === 'all' ? true
      : roleF === 'employee' ? (u.role !== 'admin' && u.role !== 'marketing' && u.role !== 'client')
      : u.role === roleF;
    if (!inRole) return false;
    const q = userQ.trim().toLowerCase();
    if (!q) return true;
    return [u.email, u.name, (u as any).nickname, companyName(u.company_id)]
      .some(v => String(v || '').toLowerCase().includes(q));
  });

  const addCompany = async () => {
    if (!newCompany.trim()) return;
    await api.adminCreateCompany(newCompany.trim()).catch((e: any) => setErr(e.message));
    setNewCompany(''); load();
  };
  const deleteCompany = async (c: Company) => {
    if (window.confirm(t('confirm_delete_company'))) { await api.adminDeleteCompany(c.id); load(); }
  };
  const deleteUser = async (u: AdminUser) => {
    if (window.confirm(t('confirm_delete_user'))) {
      await api.adminDeleteUser(u.id).catch((e: any) => setErr(e.message)); load();
    }
  };

  const tabBtn = (key: 'users' | 'companies' | 'roles' | 'jobs' | 'holidays' | 'contacts' | 'chat' | 'notify' | 'templates' | 'kb' | 'portal' | 'alerts' | 'audit', label: string) => (
    <button onClick={() => setTab(key)} style={{ padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderBottom: tab === key ? '2px solid var(--accent)' : '2px solid transparent', background: 'none', color: tab === key ? 'var(--text-primary)' : 'var(--text-secondary)', cursor: 'pointer' }}>{label}</button>
  );
  // 11 → 12px (Calem, report 66c9d174, 2nd opening: "Do the same for all other menus in the SLF system"). Then Active
  // Jobs' headings, as every staff list's (report 4bd4ae4e, 2nd opening: "Make all menus in the SLF system look like
  // this: the same height, text color, no bold, horizontal line colors etc."): black, --text-primary, at the regular
  // weight, where they were light grey at semibold (600), 10px above and below where it was 8, on one 16px line, on
  // white, over the darker --border-strong line — a 37px band, as every list's is.
  // 10 → 5px under the heading text (report c151d269: "Bring the heading text 5 pixels closer to the grey line
  // below. Do this for every page." — see Active Jobs' th; the other table in this file went with it).
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px 5px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-surface)' };
  const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '8px 10px', borderBottom: '1px solid var(--border)' };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} /></button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{t('admin_panel')}</div>
        <div style={{ flex: 1 }} />
        <button onClick={() => navigate('/')} style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), padding: '6px 12px' }}>{t('back_to_portal')}</button>
      </header>

      {editingCompany ? (
        <div style={{ flex: 1, minHeight: 0 }}>
          <CompanySettings companyId={editingCompany.id} companyName={editingCompany.name} onBack={() => { setEditingCompany(null); load(); }} onChanged={load} />
        </div>
      ) : (
      <>
      <div style={{ display: 'flex', gap: 4, padding: '0 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        {tabBtn('users', t('tab_users'))}
        {tabBtn('companies', t('tab_companies'))}
        {tabBtn('roles', t('tab_roles'))}
        {tabBtn('jobs', t('tab_jobs'))}
        {tabBtn('holidays', t('tab_holidays'))}
        {tabBtn('contacts', t('tab_contacts'))}
        {tabBtn('chat', t('tab_chat'))}
        {tabBtn('notify', t('tab_notify'))}
        {tabBtn('templates', t('tab_templates'))}
        {tabBtn('kb', t('tab_kb'))}
        {tabBtn('portal', 'Client Portal')}
          {tabBtn('audit', 'Audit Log')}
        {tabBtn('alerts', 'Error Alerts')}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 12 }}>{err}</div>}

        {tab === 'users' && (
          <div style={{ maxWidth: 980, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 8, overflow: 'hidden' }}>
                {([['all', t('role_all')], ['admin', t('role_admin')], ['employee', t('role_employee')], ['marketing', t('role_marketing')], ['client', t('role_client')]] as const).map(([k, label], i) => (
                  <button key={k} onClick={() => setRoleF(k as any)} style={{
                    padding: '6px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: 'none',
                    borderRight: i < 4 ? '1px solid var(--border)' : 'none',
                    background: roleF === k ? 'var(--bg-active)' : 'var(--bg-surface)',
                    color: roleF === k ? 'var(--accent)' : 'var(--text-secondary)',
                  }}>{label}{k === 'all' ? ` (${users.length})` : ` (${roleCount(k)})`}</button>
                ))}
              </span>
              <input value={userQ} onChange={e => setUserQ(e.target.value)} placeholder={t('adm_search_user')}
                style={{ ...inputStyle, width: 260, padding: '7px 10px' }} />
              <div style={{ flex: 1 }} />
              <button onClick={() => { setEditing(null); setShowEditor(true); }} style={btn('var(--accent)')}>
                <i className="ti ti-plus" style={{ fontSize: 13, marginRight: 6 }} aria-hidden />{t('add_employee')}
              </button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              <thead><tr>
                <th style={th}>{t('emailAddress')}</th><th style={th}>{t('name')}</th><th style={th}>{t('role')}</th>
                <th style={th}>{t('company')}</th><th style={th}>{t('modules')}</th><th style={th}>{t('status')}</th><th style={th}></th>
              </tr></thead>
              <tbody>
                {shownUsers.map(u => (
                  <tr key={u.id}>
                    <td style={td}>{u.email}</td>
                    <td style={td}>{u.name || '—'}{(u as any).nickname && (u as any).nickname !== u.name ? <span style={{ color: 'var(--accent)', fontWeight: 600 }}> ({(u as any).nickname})</span> : null}</td>
                    <td style={td}>
                      <span style={isPortalLogin(u.role)
                        ? { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#EFF6FF', color: '#1D4ED8' }
                        : undefined}>{roleLabel(u.role)}</span>
                    </td>
                    <td style={td}>{companyName(u.company_id)}</td>
                    <td style={td}>{u.role === 'admin' ? t('all') : isPortalLogin(u.role) ? '—' : u.modules.length}</td>
                    <td style={td}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: u.active ? '#E4F5EC' : '#FCEBEB', color: u.active ? '#1D9E75' : '#E24B4A' }}>
                        {u.active ? t('active') : t('disabled')}
                      </span>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => { setEditing(u); setShowEditor(true); }} title={t('edit')} style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', marginRight: 8 }}><i className="ti ti-pencil" /></button>
                      {u.id !== me?.userId && (
                        <button onClick={() => deleteUser(u)} title={t('delete')} style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
                      )}
                    </td>
                  </tr>
                ))}
                {shownUsers.length === 0 && (
                  <tr><td style={{ ...td, color: 'var(--text-tertiary)' }} colSpan={7}>{t('adm_no_user')}</td></tr>
                )}
              </tbody>
            </table>
            <PeopleSettings />
            <NameCheckAllowances />
          </div>
        )}

        {tab === 'companies' && (
          <div style={{ maxWidth: 640, margin: '0 auto' }}>
            <WorkspaceSettings />
            <BankMailboxSettings />
            <ConsultCalendarSettings />
            <MarketCitiesCard />
            <CountryFeaturesCard />
            {/* Its setting is read through the Due Diligence routes, and a refused call pages the admins, so an admin
                an owner-only rule keeps out of that module gets no card. */}
            {(me?.modules || []).includes('due_diligence') && <PoaMadeAt />}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input value={newCompany} onChange={e => setNewCompany(e.target.value)} placeholder={t('company_name')} onKeyDown={e => e.key === 'Enter' && addCompany()} style={inputStyle} />
              <button onClick={addCompany} style={{ ...btn('var(--accent)'), whiteSpace: 'nowrap' }}>{t('add_company')}</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
              <thead><tr><th style={th}>{t('company')}</th><th style={th}>{t('members')}</th><th style={th}></th></tr></thead>
              <tbody>
                {companies.map(c => (
                  <tr key={c.id}>
                    <td style={td}>
                      <button onClick={() => setEditingCompany(c)} style={{ border: 'none', background: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 }}>{c.name}</button>
                    </td>
                    <td style={td}>{c.userCount ?? 0}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {isAccountingFirm(c) && <span style={{ marginRight: 8, verticalAlign: 'middle' }}><TelegramBind admin iconOnly /></span>}
                      <button onClick={() => setEditingCompany(c)} title={t('cs_settings')} style={{ border: 'none', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', marginRight: 8 }}><i className="ti ti-settings" /></button>
                      <button onClick={() => deleteCompany(c)} title={t('delete')} style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
                    </td>
                  </tr>
                ))}
                {companies.length === 0 && <tr><td style={td} colSpan={3}>{t('none')}</td></tr>}
              </tbody>
            </table>
            <TeamCalendars />
          </div>
        )}

        {tab === 'roles' && <Roles />}
        {tab === 'jobs' && <JobResPills />}
        {tab === 'jobs' && <JobRequiredDocs />}
        {tab === 'jobs' && <RemovedJobs />}
        {tab === 'holidays' && <ThaiHolidays />}
        {tab === 'contacts' && <ContactBranches />}
        {tab === 'chat' && <ChatDocGroup />}
        {tab === 'chat' && <ChatPins />}
        {tab === 'chat' && <ChatJobsGroup />}
        {tab === 'notify' && <NotifyPanel />}
        {tab === 'templates' && <EmailTemplates />}
        {tab === 'kb' && <KnowledgeBase />}
        {tab === 'portal' && <PortalClients />}
        {tab === 'audit' && <DocAuditPanel />}
        {tab === 'alerts' && <ErrorAlerts />}
      </div>
      </>
      )}

      {showEditor && (
        <UserEditor user={editing} companies={companies} onClose={() => setShowEditor(false)} onSaved={load} />
      )}
    </div>
  );
}

// Notifications: test-mode switch + approval queue for all automatic emails / TG messages.
function NotifyPanel() {
  const { t } = useTranslation();
  const [auto, setAuto] = useState(false);
  const [bankSend, setBankSend] = useState(true);
  const [joyOn, setJoyOn] = useState(true);
  const [joyAllowed, setJoyAllowed] = useState<string[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [fyMonth, setFyMonth] = useState(1);   // org-default fiscal-year start month/day
  const [fyDay, setFyDay] = useState(1);
  const [fyLabel, setFyLabel] = useState('');
  const [ssoRate, setSsoRate] = useState(5);   // SSO contribution config
  const [ssoCap, setSsoCap] = useState(875);
  const [ssoFloor, setSsoFloor] = useState(1650);
  const [pnd1, setPnd1] = useState<{ expenseRate: number; expenseCap: number; ssoCap: number; deductions: { name: string; amount: number }[] }>({ expenseRate: 50, expenseCap: 100000, ssoCap: 9000, deductions: [] });   // PND.1 config
  const [whtTypes, setWhtTypes] = useState<{ label: string; rate: number }[]>([]);   // WHT income-type → rate catalog
  // Notification browse state. The queue was retired 2026-08-01, so this list is now a HISTORY of what
  // was sent / discarded as much as a list of anything waiting — hence status is a filter, not a fixed
  // 'pending', and the table is paged (rendering every row at once once froze the page at 1,500).
  const [nStatus, setNStatus] = useState('pending');
  const [nKind, setNKind] = useState('all');
  const [nChannel, setNChannel] = useState('all');
  const [nQ, setNQ] = useState('');
  const [nPage, setNPage] = useState(1);
  const [nTotal, setNTotal] = useState(0);
  const [nLimit, setNLimit] = useState(100);
  const [nKinds, setNKinds] = useState<string[]>([]);
  const [nCounts, setNCounts] = useState<Record<string, number>>({});
  const load = () => { setLoading(true); Promise.all([api.adminGetNotifySettings(), api.adminGetNotifications({ status: nStatus, kind: nKind, channel: nChannel, q: nQ, page: nPage }), api.adminGetUsers(), api.adminGetFyDefault(), api.adminGetSsoConfig(), api.adminGetPnd1Config(), api.adminGetWhtConfig()]).then(([s, n, u, fy, sso, p, w]: any[]) => { setAuto(!!s.auto); setBankSend(s.bankAutosend !== false); setJoyOn(s.joyEnabled !== false); setJoyAllowed((s.joyAllowedEmails || []).map((e: string) => e.toLowerCase())); setUsers(Array.isArray(u) ? u : (u.rows || [])); setRows(n.rows || []); setNTotal(n.total || 0); setNLimit(n.limit || 100); setNKinds(n.kinds || []); setNCounts(n.counts || {}); setFyMonth(fy?.month || 1); setFyDay(fy?.day || 1); setFyLabel(fy?.label || ''); setSsoRate(sso?.rate ?? 5); setSsoCap(sso?.cap ?? 875); setSsoFloor(sso?.floor ?? 1650); if (p) setPnd1({ expenseRate: p.expenseRate, expenseCap: p.expenseCap, ssoCap: p.ssoCap, deductions: p.deductions || [] }); setWhtTypes(w?.incomeTypes || []); }).catch(() => {}).finally(() => setLoading(false)); };
  const saveWht = async (list: { label: string; rate: number }[]) => { setWhtTypes(list); try { await api.adminSetWhtConfig(list); } catch (e: any) { window.alert(e.message); } };
  const saveFy = async (month: number, day: number) => { setFyMonth(month); setFyDay(day); try { const r: any = await api.adminSetFyDefault(month, day); setFyLabel(r?.label || ''); } catch (e: any) { window.alert(e.message); } };
  const saveSso = async (rate: number, cap: number, floor: number) => { setSsoRate(rate); setSsoCap(cap); setSsoFloor(floor); try { await api.adminSetSsoConfig(rate, cap, floor); } catch (e: any) { window.alert(e.message); } };
  const savePnd1 = async (next: typeof pnd1) => { setPnd1(next); try { await api.adminSetPnd1Config(next); } catch (e: any) { window.alert(e.message); } };
  useEffect(() => { load(); }, []);
  // Any filter change resets to page 1; a page change keeps the filter.
  useEffect(() => { setNPage(1); }, [nStatus, nKind, nChannel, nQ]);
  useEffect(() => { load(); }, [nStatus, nKind, nChannel, nQ, nPage]);   // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async () => { const next = !auto; if (next && !window.confirm(t('nt_auto_confirm'))) return; setAuto(next); try { await api.adminSetNotifyAuto(next); } catch { /* */ } };
  const toggleBank = async () => { const next = !bankSend; setBankSend(next); try { await api.adminSetBankAutosend(next); } catch { /* */ } };
  const toggleJoy = async () => { const next = !joyOn; setJoyOn(next); try { await api.adminSetJoyEnabled(next); } catch { /* */ } };
  const toggleJoyUser = async (email: string) => { const e = email.toLowerCase(); const next = joyAllowed.includes(e) ? joyAllowed.filter(x => x !== e) : [...joyAllowed, e]; setJoyAllowed(next); try { await api.adminSetJoyAllowed(next); } catch { /* */ } };
  const send = async (id: string) => { setBusy(true); try { await api.adminSendNotification(id); load(); } catch (e: any) { window.alert(e.message); } finally { setBusy(false); } };
  const discard = async (id: string) => { if (!window.confirm(t('nt_discard_confirm'))) return; setBusy(true); try { await api.adminDiscardNotification(id); load(); } finally { setBusy(false); } };
  const sendAll = async () => { if (!window.confirm(t('nt_sendall_confirm'))) return; setBusy(true); try { const r: any = await api.adminSendAllNotifications(); window.alert(t('nt_sendall_done', { n: r.sent })); load(); } finally { setBusy(false); } };
  // Retire everything matching the current filter. Discard only — there is no bulk send, on purpose.
  const discardAll = async () => {
    if (!window.confirm(t('nt_discard_matching_confirm', { n: nTotal }))) return;
    setBusy(true);
    try { const r: any = await api.adminDiscardMatching({ kind: nKind, channel: nChannel, q: nQ }); window.alert(t('nt_discard_matching_done', { n: r.discarded })); load(); }
    catch (e: any) { window.alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 18, background: auto ? '#F4FBF7' : '#FFFBEB' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: auto ? '#15803D' : '#B45309' }}>{auto ? t('nt_mode_auto') : t('nt_mode_test')}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>{auto ? t('nt_auto_desc') : t('nt_test_desc')}</div>
          </div>
          <button onClick={toggle} style={{ ...btn(auto ? 'var(--c-gray-200)' : '#1E9E63', auto ? 'var(--text-secondary)' : '#fff'), whiteSpace: 'nowrap' }}>
            {auto ? t('nt_switch_to_test') : t('nt_switch_to_auto')}
          </button>
        </div>
      </div>

      {/* Org default fiscal (tax) year — per-client overrides live on each client record. */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <i className="ti ti-calendar-stats" style={{ fontSize: 20, color: 'var(--text-secondary)' }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>Default fiscal (tax) year</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>Applies to every client that hasn't set its own. Annual invoicing and annual compliance tasks fire on the 1st of the fiscal year-end month. Per-client overrides (e.g. Langwill = Dec 1 → Nov 30) are set on the client record.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Starts</span>
            <select value={fyMonth} onChange={e => saveFy(Number(e.target.value), fyDay)} style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }}>
              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <input type="number" min={1} max={31} value={fyDay} onChange={e => setFyDay(Number(e.target.value) || 1)} onBlur={e => saveFy(fyMonth, Number(e.target.value) || 1)} style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, background: '#fff', width: 64 }} />
            {fyLabel && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>({fyLabel})</span>}
          </div>
        </div>
      </div>

      {/* SSO (Social Security) contribution config — used by the Tax module's SSO calculation. */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <i className="ti ti-shield-half" style={{ fontSize: 20, color: 'var(--text-secondary)' }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>Social Security (SSO) rates</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>Used by the Tax module's SSO calculation. Each employee (and the employer, matching) pays this % of wage, capped per person, with a wage floor. Defaults follow the firm's sheet: 5% / 875 / 1,650.</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Rate %
              <input type="number" min={0} step={0.5} value={ssoRate} onChange={e => setSsoRate(Number(e.target.value))} onBlur={e => saveSso(Number(e.target.value) || 5, ssoCap, ssoFloor)} style={{ marginLeft: 6, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, width: 64 }} /></label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Cap {currencySymbol()}
              <input type="number" min={1} value={ssoCap} onChange={e => setSsoCap(Number(e.target.value))} onBlur={e => saveSso(ssoRate, Number(e.target.value) || 875, ssoFloor)} style={{ marginLeft: 6, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, width: 80 }} /></label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Floor {currencySymbol()}
              <input type="number" min={0} value={ssoFloor} onChange={e => setSsoFloor(Number(e.target.value))} onBlur={e => saveSso(ssoRate, ssoCap, Number(e.target.value) || 1650)} style={{ marginLeft: 6, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, width: 80 }} /></label>
          </div>
        </div>
      </div>

      {/* PND.1 (payroll withholding) deductions config — used by the Tax module's PND.1 calc. */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <i className="ti ti-receipt-2" style={{ fontSize: 20, color: 'var(--text-secondary)' }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>PND.1 payroll tax — deductions</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>Net assessable = salary×12 − expense deduction − Σ allowance items − SSO → progressive brackets ÷ 12. The allowance items below are subtracted for every employee (Personal Allowance is the default; add spouse / children / insurance / provident fund …).</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Expense %<input type="number" min={0} value={pnd1.expenseRate} onChange={e => setPnd1({ ...pnd1, expenseRate: Number(e.target.value) })} onBlur={() => savePnd1(pnd1)} style={{ marginLeft: 6, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, width: 56 }} /></label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Exp cap {currencySymbol()}<input type="number" min={0} value={pnd1.expenseCap} onChange={e => setPnd1({ ...pnd1, expenseCap: Number(e.target.value) })} onBlur={() => savePnd1(pnd1)} style={{ marginLeft: 6, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, width: 90 }} /></label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>SSO cap {currencySymbol()}<input type="number" min={0} value={pnd1.ssoCap} onChange={e => setPnd1({ ...pnd1, ssoCap: Number(e.target.value) })} onBlur={() => savePnd1(pnd1)} style={{ marginLeft: 6, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, width: 80 }} /></label>
          </div>
        </div>
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700 }}>Allowance items (subtracted from income)</div>
            <div style={{ flex: 1 }} />
            <button onClick={() => savePnd1({ ...pnd1, deductions: [...pnd1.deductions, { name: 'New allowance', amount: 0 }] })} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}><i className="ti ti-plus" /> Add</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pnd1.deductions.map((d, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input value={d.name} onChange={e => setPnd1({ ...pnd1, deductions: pnd1.deductions.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} onBlur={() => savePnd1(pnd1)} style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8 }} />
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{currencySymbol()}</span>
                <input type="number" min={0} value={d.amount} onChange={e => setPnd1({ ...pnd1, deductions: pnd1.deductions.map((x, j) => j === i ? { ...x, amount: Number(e.target.value) } : x) })} onBlur={() => savePnd1(pnd1)} style={{ width: 110, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, textAlign: 'right' }} />
                <button onClick={() => savePnd1({ ...pnd1, deductions: pnd1.deductions.filter((_, j) => j !== i) })} title="Remove" style={{ border: 'none', background: 'transparent', color: '#C2410C', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* WHT (PND.3/53) income-type → rate catalog — used by the Tax module's WHT ledger. */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <i className="ti ti-scissors" style={{ fontSize: 20, color: 'var(--text-secondary)' }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>WHT income types &amp; rates (PND.3 / 53)</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>The income-type dropdown + default rates used in the Tax module's WHT ledger. Edit, add or remove.</div>
          </div>
          <button onClick={() => saveWht([...whtTypes, { label: 'New type', rate: 3 }])} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' }}><i className="ti ti-plus" /> Add</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {whtTypes.map((tp, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input value={tp.label} onChange={e => setWhtTypes(whtTypes.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} onBlur={() => saveWht(whtTypes)} style={{ flex: 1, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8 }} />
              <input type="number" min={0} step={0.5} value={tp.rate} onChange={e => setWhtTypes(whtTypes.map((x, j) => j === i ? { ...x, rate: Number(e.target.value) } : x))} onBlur={() => saveWht(whtTypes)} style={{ width: 70, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 8, textAlign: 'right' }} />
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>%</span>
              <button onClick={() => saveWht(whtTypes.filter((_, j) => j !== i))} title="Remove" style={{ border: 'none', background: 'transparent', color: '#C2410C', cursor: 'pointer' }}><i className="ti ti-trash" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Bank statement module — its own independent email switch (default ON). */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 18, background: bankSend ? '#F4FBF7' : '#FFFBEB' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <i className="ti ti-building-bank" style={{ fontSize: 20, color: bankSend ? '#15803D' : '#B45309' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: bankSend ? '#15803D' : '#B45309' }}>Bank statement emails: {bankSend ? 'ON' : 'OFF'}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>Auto-emails from the AI Bank mailbox (reply to non-bank senders, forward unreadable statements to the “Bank statement unreadable — forward to” role, notify the office on success). Independent of the mode above. Statement import always runs either way.</div>
          </div>
          <button onClick={toggleBank} style={{ ...btn(bankSend ? 'var(--c-gray-200)' : '#1E9E63', bankSend ? 'var(--text-secondary)' : '#fff'), whiteSpace: 'nowrap' }}>
            {bankSend ? 'Turn off' : 'Turn on'}
          </button>
        </div>
      </div>

      {/* Joy — virtual accountant. Default ON; hides her floating avatar + pauses her supervision when off. */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 18, background: joyOn ? '#F4FBF7' : '#FFFBEB' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <i className="ti ti-user-heart" style={{ fontSize: 20, color: joyOn ? '#15803D' : '#B45309' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: joyOn ? '#15803D' : '#B45309' }}>Joy (virtual accountant): {joyOn ? 'ON' : 'OFF'}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 2 }}>The floating Joy avatar shown to office users and her supervision (nudges, questions, status updates). When OFF, the avatar hides and Joy pauses — the underlying compliance / invoice crons keep running regardless.</div>
          </div>
          <button onClick={toggleJoy} style={{ ...btn(joyOn ? 'var(--c-gray-200)' : '#1E9E63', joyOn ? 'var(--text-secondary)' : '#fff'), whiteSpace: 'nowrap' }}>
            {joyOn ? 'Turn off' : 'Turn on'}
          </button>
        </div>
        {/* Who can see Joy (she's in development — pick the staff who get her). joy@ kiosk always allowed. */}
        <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>Who can see Joy</div>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 8 }}>She's in development — only the staff you tick here see her. Leave all unticked to hide her from everyone.</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {users.filter(u => u.role !== 'external' && (u.email || '')).map(u => {
              const e = (u.email || '').toLowerCase(); const on = joyAllowed.includes(e);
              return (
                <button key={u.id} onClick={() => toggleJoyUser(u.email)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid', borderColor: on ? '#1E9E63' : 'var(--border-strong)', background: on ? '#F4FBF7' : 'transparent', color: on ? '#15803D' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <i className={`ti ${on ? 'ti-check' : 'ti-plus'}`} style={{ fontSize: 12 }} />{u.name || u.email}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t('nt_pending', { n: nTotal })}</div>
        {/* Status counts double as the filter — the fastest way to answer "did that go out?" */}
        {(['pending', 'sent', 'discarded', 'all'] as const).map(st => (
          <button key={st} onClick={() => setNStatus(st)}
            style={{ padding: '4px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${nStatus === st ? 'var(--accent)' : 'var(--border)'}`,
              background: nStatus === st ? 'var(--accent-bg)' : 'transparent',
              color: nStatus === st ? 'var(--accent-text)' : 'var(--text-secondary)' }}>
            {t(`nt_st_${st}`)}{st !== 'all' && nCounts[st] != null ? ` ${nCounts[st]}` : ''}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        {nStatus === 'pending' && nTotal > 0 && (
          <button onClick={discardAll} disabled={busy} style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), padding: '6px 12px' }}>
            {t('nt_discard_matching', { n: nTotal })}
          </button>
        )}
        {nStatus === 'pending' && nTotal > 0 && <button onClick={sendAll} disabled={busy} style={btn('var(--accent)')}>{t('nt_sendall')}</button>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <select value={nKind} onChange={e => setNKind(e.target.value)} style={{ padding: '6px 9px', fontSize: 12, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)' }}>
          <option value="all">{t('nt_all_kinds')}</option>
          {nKinds.map(k => <option key={k} value={k}>{k}</option>)}
        </select>
        <select value={nChannel} onChange={e => setNChannel(e.target.value)} style={{ padding: '6px 9px', fontSize: 12, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)' }}>
          <option value="all">{t('nt_all_channels')}</option>
          <option value="email">Email</option>
          <option value="tg">Telegram</option>
        </select>
        <input value={nQ} onChange={e => setNQ(e.target.value)} placeholder={t('nt_search')}
          style={{ padding: '6px 10px', fontSize: 12, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-primary)', width: 240 }} />
      </div>

      {loading ? <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>
        : rows.length === 0 ? <div style={{ padding: 28, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('nt_none')}</div>
          : (
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              {rows.map((r: any) => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                  <i className={`ti ti-${r.channel === 'tg' ? 'brand-telegram' : 'mail'}`} style={{ fontSize: 16, color: 'var(--text-tertiary)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.summary || r.subject || r.kind}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{r.channel === 'email' ? (r.to || []).join(', ') : 'Telegram'} · {r.kind}</div>
                  </div>
                  {/* Only a pending row can still be acted on; sent/discarded rows are history. */}
                  {r.status === 'pending' ? (<>
                    <button onClick={() => send(r.id)} disabled={busy} style={{ ...btn('#1E9E63'), padding: '6px 12px' }}>{t('nt_send')}</button>
                    <button onClick={() => discard(r.id)} disabled={busy} style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), padding: '6px 12px' }}>{t('nt_discard')}</button>
                  </>) : (
                    <span style={{ fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', padding: '3px 9px', borderRadius: 999,
                      background: r.status === 'sent' ? '#E7F6EE' : 'var(--c-gray-100)',
                      color: r.status === 'sent' ? '#1D7C4D' : 'var(--text-tertiary)' }}>
                      {t(`nt_st_${r.status}`)}{r.sent_by_name ? ` · ${r.sent_by_name}` : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
      <Pagination page={nPage} total={nTotal} limit={nLimit} onPage={p => setNPage(p)} />
    </div>
  );
}

// ── Document audit stream (owner 2026-09-15): the write-only log finally has a reader. ─────────
function DocAuditPanel() {
  const [rows, setRows] = React.useState<any[]>([]);
  const [q, setQ] = React.useState('');
  const [kind, setKind] = React.useState('');
  const [action, setAction] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  // Only the latest request may fill the table, and searchedQ holds the words it asked for (see the effect below).
  const loadSeq = React.useRef(0);
  const searchedQ = React.useRef('');
  const load = () => {
    const seq = ++loadSeq.current;
    searchedQ.current = q.trim();
    setLoading(true);
    const p = `?q=${encodeURIComponent(q)}&kind=${kind}&action=${action}&limit=200`;
    api.adminDocAudit(p).then((d: any) => { if (seq === loadSeq.current) setRows(d.rows || []); }).catch(() => { if (seq === loadSeq.current) setRows([]); }).finally(() => { if (seq === loadSeq.current) setLoading(false); });
  };
  React.useEffect(() => { load(); }, [kind, action]);   // eslint-disable-line
  // The search box searches as you type, 300ms after the last key, and Enter still searches at once (Calem, report
  // 0c04879b); emptied, it brings back the newest 200 unfiltered. It searched on Enter only, and said so ("— Enter").
  // Words a load has already asked for are not asked for again, nor with only a space added (the server trims them).
  React.useEffect(() => {
    const h = setTimeout(() => { if (q.trim() !== searchedQ.current) load(); }, 300);
    return () => clearTimeout(h);
  }, [q]);   // eslint-disable-line
  const fmt = (ts: number) => { const d = new Date(ts * 1000); const p = (n: number) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`; };
  const inp: React.CSSProperties = { padding: '6px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' };
  // 11 → 12px (Calem, report 66c9d174, 2nd opening — see the Users list's th); black, regular weight, 10px above and
  // below on a 16px line, white, the darker line, as that th (report 4bd4ae4e).
  // 10 → 5px under the heading text (report c151d269 — see the th above).
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px 5px', lineHeight: '16px', borderBottom: '1px solid var(--border-strong)', whiteSpace: 'nowrap', background: 'var(--bg-surface)' };
  const td: React.CSSProperties = { fontSize: 12.5, color: 'var(--text-primary)', padding: '7px 10px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' };
  const ACT_COLOR: Record<string, string> = { delete: '#B42318', void: '#B42318', release: '#B45309', edit: '#B45309', combine_payment: '#0E7C66', uncombine_payment: '#B45309', restore: '#0E7C66', cancel: '#B42318' };
  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Doc no / client / user / reason" style={{ ...inp, width: 280 }} />
        <select value={kind} onChange={e => setKind(e.target.value)} style={inp}>
          <option value="">All documents</option><option value="receipt">Receipts</option><option value="invoice">Invoices</option><option value="quotation">Quotations</option>
        </select>
        <select value={action} onChange={e => setAction(e.target.value)} style={inp}>
          <option value="">All actions</option><option value="delete">Delete</option><option value="edit">Edit</option><option value="void">Void</option><option value="release">Release</option><option value="combine_payment">Combine payment</option><option value="uncombine_payment">Uncombine</option>
        </select>
        {loading && <i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} />}
        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>Newest 200 shown — every delete / edit / void / combine on client documents, firm side and portal side alike.</span>
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'auto', maxHeight: '70vh' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>When</th><th style={th}>Document</th><th style={th}>Action</th><th style={th}>Client</th><th style={th}>Amount</th><th style={th}>By</th><th style={th}>Reason</th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.at)}</td>
                <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700 }}>{r.doc_no || r.doc_kind}</td>
                <td style={{ ...td, color: ACT_COLOR[r.action] || 'var(--text-secondary)', fontWeight: 700 }}>{r.action}</td>
                <td style={td}>{r.client_name}</td>
                <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(r.amount)}</td>
                <td style={td}>{r.by_name}</td>
                <td style={{ ...td, maxWidth: 420, whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>{r.reason}</td>
              </tr>
            ))}
            {!rows.length && !loading && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)', padding: 24 }}>No audit rows match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
